'use client';

import {useState, useEffect} from 'react';

import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Skeleton from '@mui/material/Skeleton';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';

import { useTranslate } from 'src/locales';
import {getRecipeListForUser} from 'src/services/recipe/user-list-api';
import type {RecipeOutput} from 'src/types/backend/generated';
import RecipeTile from 'src/components/recipe/recipe-tile';

// ----------------------------------------------------------------------

type Props = {
  userId: number;
  totalRecipeCount: number;
};

// ----------------------------------------------------------------------

export function UserRecipesList({userId, totalRecipeCount}: Props) {
  const { t } = useTranslate();
  const [recipes, setRecipes] = useState<RecipeOutput[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Jeśli użytkownik nie ma przepisów, nie ładuj danych
  const hasRecipes = totalRecipeCount > 0;

  // Liczba skeletonów do wyświetlenia - max 3
  const skeletonCount = Math.min(totalRecipeCount, 3);

  useEffect(() => {
    // Jeśli brak przepisów, nie ładuj danych
    if (!hasRecipes) {
      setIsLoading(false);
      return;
    }

    const fetchRecipes = async () => {
      try {
        setIsLoading(true);
        const result = await getRecipeListForUser({
          userId,
          size: 3,
          from: 0,
          sortBy: 'creationDate',
          sortOrder: 'desc',
        });

        if (result) {
          setRecipes(result.items);
        }
      } catch (err) {
        console.error('Error fetching user recipes:', err);
        setError(err instanceof Error ? err.message : t('page.user.profile.recipes.error_loading') as string);
      } finally {
        setIsLoading(false);
      }
    };

    fetchRecipes();
  }, [userId, hasRecipes, t]);

  if (isLoading) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {t('page.user.profile.recipes.title')}
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{mb: 3}}>
            {t('page.user.profile.recipes.description')}
          </Typography>

          <Box
            gap={3}
            display="grid"
            gridTemplateColumns={{
              xs: 'repeat(1, 1fr)',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(3, 1fr)',
            }}
          >
            {Array.from({length: skeletonCount}).map((_, index) => (
              <Box key={index}>
                <Skeleton variant="rectangular" height={200} sx={{borderRadius: 1}}/>
                <Skeleton variant="text" width="80%" sx={{mt: 1}}/>
                <Skeleton variant="text" width="60%"/>
              </Box>
            ))}
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {t('page.user.profile.recipes.title')}
          </Typography>
          <Typography variant="body2" color="error">
            {error}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  if (recipes.length === 0) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {t('page.user.profile.recipes.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('page.user.profile.recipes.no_recipes')}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {t('page.user.profile.recipes.title')}
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{mb: 3}}>
          {t('page.user.profile.recipes.description')}
        </Typography>

        <Box
          gap={3}
          display="grid"
          gridTemplateColumns={{
            xs: 'repeat(1, 1fr)',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(3, 1fr)',
          }}
        >
          {recipes.map((recipe) => (
            <RecipeTile
              key={`recipe-${recipe.pimcoreRecipeId}`}
              recipe={recipe}
            />
          ))}
        </Box>

        {totalRecipeCount > 3 && (
          <Box sx={{mt: 3, textAlign: 'center'}}>
            <Button variant="outlined" size="small">
              {t('page.user.profile.recipes.see_all')}
            </Button>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

